import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Éclat-Gen',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/og-image.jpg`,
    width: 1200,
    height: 630,
  },
  description:
    'Éclat-Gen is a premier AI strategic consultancy engineering brilliance through Digital Biology, Digital Transformation, and Digital Wellness.',
  sameAs: [],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    availableLanguage: 'English',
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'Éclat-Gen',
  description:
    'Brilliance. Engineered. — Premier AI strategic consultancy for Digital Biology, Digital Transformation, and Digital Wellness.',
  publisher: { '@id': `${SITE_URL}/#organization` },
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'Éclat-Gen — Brilliance. Engineered. | AI Strategic Consulting',
  description:
    'Premier AI strategic consultancy engineering brilliance through Digital Biology, Digital Transformation, and Digital Wellness.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en-US',
  dateModified: '2026-03-26',
};

const professionalServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  '@id': `${SITE_URL}/#professionalservice`,
  name: 'Éclat-Gen',
  url: SITE_URL,
  description:
    'AI strategic consultancy specialising in Digital Biology, Digital Transformation, and Digital Wellness for biotech, finance, labs, and logistics organisations.',
  serviceType: ['AI Strategic Consulting', 'Digital Transformation', 'Digital Biology', 'Digital Wellness'],
  areaServed: 'Worldwide',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Éclat-Gen Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Digital Biology',
          description:
            'AI-powered solutions for target identification, formulation optimisation, clinical development, biomarker discovery, and cGMP manufacturing.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Digital Transformation',
          description:
            'Machine learning, deep learning, advanced analytics, and AI automation to modernise enterprise operations and intelligent systems.',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Digital Wellness',
          description:
            'AI-driven health consultancy, functional food intelligence, and bio-sensor technology for holistic wellbeing optimisation.',
        },
      },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is Éclat-Gen?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Éclat-Gen is a premier AI strategic consultancy that engineers brilliance through Digital Biology, Digital Transformation, and Digital Wellness, empowering organisations to illuminate, innovate, and elevate.',
      },
    },
    {
      '@type': 'Question',
      name: 'What services does Éclat-Gen offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Éclat-Gen offers three core service pillars: Digital Biology (AI-powered drug discovery and biotech solutions), Digital Transformation (ML, deep learning, analytics, and AI automation), and Digital Wellness (health consultancy, functional food intelligence, and bio-sensor technology).',
      },
    },
    {
      '@type': 'Question',
      name: 'Who are Éclat-Gen\'s clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Éclat-Gen serves organisations across biotech, finance, research laboratories, and logistics sectors that seek intelligent AI-driven transformation and strategic advisory.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is Digital Biology at Éclat-Gen?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Digital Biology at Éclat-Gen applies AI to life sciences workflows including target identification, formulation optimisation, biomarker discovery, clinical development, and cGMP manufacturing processes.',
      },
    },
  ],
};

function injectSchema(id: string, schema: object): void {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
}

export default function SEOHead() {
  useEffect(() => {
    injectSchema('schema-organization', organizationSchema);
    injectSchema('schema-website', websiteSchema);
    injectSchema('schema-webpage', webPageSchema);
    injectSchema('schema-professional-service', professionalServiceSchema);
    injectSchema('schema-faq', faqSchema);

    return () => {
      ['schema-organization', 'schema-website', 'schema-webpage', 'schema-professional-service', 'schema-faq'].forEach(
        (id) => document.getElementById(id)?.remove()
      );
    };
  }, []);

  return null;
}
